import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import _get from 'lodash/get';

import { getThemeProps } from 'ravenjs/utils/theme';
import { DEFAULT_COLORS } from 'constants/colors';
import Link from '../../Link';

const TitleFieldStyled = styled.label`
    ${({ showRequired, color }) => `
         color : ${showRequired ? DEFAULT_COLORS.RED : color};
    `};
    ${({ fontSize, fontWeight, fontFamily, margin, padding }) => `
        font-weight : ${fontWeight || 'bold'};
        font-size : ${fontSize || '16px'};
        font-family : ${fontFamily || 'Favorit'};
        margin : ${margin || '0 0 10px 15px'};
        padding : ${padding || '0'};
    `};
    /**
     * Add all of the remaining styles from the theme
     */
    ${getThemeProps('TitleField.styles')};
    /**
     * Add in the dynamic styles
     */
    ${({ root, theme }) => {
        const rootStyles = getThemeProps('TitleField.styles.root', null, { theme });

        return root ? rootStyles : null;
    }};
`;

function TitleField({ id, title, color, uiOptions, ...rest }) {
    const styles = _get(uiOptions, 'titleStyles', {});
    const actionLink = _get(uiOptions, 'actionLink', null);
    const addIdAttribute = _get(uiOptions, 'addIdAttribute', false);
    const labelProps = addIdAttribute ? { id } : {};

    if (!title) return null;

    if (actionLink && actionLink.show) {
        // useMouseDown: When true, uses onMouseDown instead of onClick.
        // This is useful when the link is near form fields, as mousedown fires
        // before blur events, preventing the "double-click" issue where the first
        // click only removes focus from the input field.
        const useMouseDown = _get(actionLink, 'useMouseDown', false);

        const handleClick = e => {
            e.preventDefault();

            if (actionLink.onClick) {
                actionLink.onClick(e);
            } else if (actionLink.scrollToId) {
                const el = document.getElementById(actionLink.scrollToId);
                if (el) {
                    const offset = actionLink.scrollOffset || 100;
                    const elementPosition = el.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - offset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth',
                    });
                }
            }
        };

        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '10px',
                }}
            >
                <TitleFieldStyled {...rest} {...labelProps} htmlFor={id} color={color} {...styles}>
                    {title}
                </TitleFieldStyled>
                <Link
                    onClick={!useMouseDown ? handleClick : undefined}
                    onMouseDown={useMouseDown ? handleClick : undefined}
                    style={{
                        fontSize: actionLink.fontSize || '14px',
                        cursor: 'pointer',
                        marginRight: '15px',
                        lineHeight: '1.2',
                        fontFamily: actionLink.fontFamily || 'Favorit',
                        ...(actionLink.style || {}),
                    }}
                >
                    {actionLink.label || 'Link'}
                </Link>
            </div>
        );
    }

    return (
        <TitleFieldStyled {...rest} {...labelProps} htmlFor={id} color={color} {...styles}>
            {title}
        </TitleFieldStyled>
    );
}

TitleField.propTypes = {
    id: PropTypes.string,
    color: PropTypes.string,
    uiOptions: PropTypes.object,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

TitleField.defaultProps = {
    id: null,
    title: null,
    uiOptions: {},
    color: DEFAULT_COLORS.GREY_19,
};

export default TitleField;
